import { useCallback, useEffect, useRef, useState } from 'react';

import { DESIGN_CANVAS, useCanvasScale } from '@/lib/canvas';
import { effectiveFreeformRect } from '@/lib/freeform';
import type { Annotation, DrawStroke, Slide } from '@/lib/schema.types';
import { cn } from '@/lib/utils';

import { strokePointsAttr } from './draw-playback';
import { StageElement } from './StageElement';
import type { TeachTool } from './TeachStudio';

interface Props {
  slide: Slide;
  /** Annotations to render on top of the elements (caller controls accumulation). */
  annotations: Annotation[];
  /** Freehand strokes to render (caller controls accumulation, like annotations). */
  drawStrokes?: DrawStroke[];
  /**
   * When non-null, restricts visible elements to this set (used during playback).
   * `null` means all elements on the slide are visible (edit mode).
   */
  revealedElementIds: Set<string> | null;
  tool: TeachTool;
  onPickElement: (elementId: string) => void;
  /** Called when a pen/highlighter stroke is finished (design-space points). */
  onStrokeComplete?: (stroke: Omit<DrawStroke, 'id'>) => void;
}

const PEN = { color: '#0ea5e9', width: 3 };
const HIGHLIGHTER = { color: '#fde047', width: 16 };

interface ElementBox {
  id: string;
  rect: { left: number; top: number; width: number; height: number };
}

export function Stage({
  slide,
  annotations,
  drawStrokes = [],
  revealedElementIds,
  tool,
  onPickElement,
  onStrokeComplete,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const elementRefs = useRef<Map<string, HTMLElement>>(new Map());
  const [boxes, setBoxes] = useState<Record<string, ElementBox['rect']>>({});
  // The stage is rendered scaled (ScaledStage). getBoundingClientRect returns
  // screen px, but the annotation overlay lives in the scaled design space, so
  // divide measured rects by the scale to keep marks aligned with elements.
  const scale = useCanvasScale();

  const measure = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const containerRect = container.getBoundingClientRect();
    const next: Record<string, ElementBox['rect']> = {};
    elementRefs.current.forEach((node, id) => {
      const r = node.getBoundingClientRect();
      next[id] = {
        left: (r.left - containerRect.left) / scale,
        top: (r.top - containerRect.top) / scale,
        width: r.width / scale,
        height: r.height / scale,
      };
    });
    setBoxes(next);
  }, [scale]);

  useEffect(() => {
    measure();
    const ro = new ResizeObserver(() => measure());
    if (containerRef.current) ro.observe(containerRef.current);
    elementRefs.current.forEach((n) => ro.observe(n));
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [measure, slide]);

  const registerElement = useCallback(
    (id: string) => (node: HTMLElement | null) => {
      if (node) elementRefs.current.set(id, node);
      else elementRefs.current.delete(id);
    },
    [],
  );

  const drawing = tool === 'pen' || tool === 'highlighter';
  // Element-picking (annotation) tools — pen/highlighter draw freely instead.
  const interactive = tool !== 'cursor' && !drawing;

  const [livePts, setLivePts] = useState<{ x: number; y: number }[] | null>(null);
  const drawingRef = useRef(false);

  const toDesignPoint = (e: React.PointerEvent) => {
    const c = containerRef.current;
    if (!c) return { x: 0, y: 0 };
    const r = c.getBoundingClientRect();
    return { x: (e.clientX - r.left) / scale, y: (e.clientY - r.top) / scale };
  };

  const onDrawDown = (e: React.PointerEvent) => {
    if (!drawing) return;
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    drawingRef.current = true;
    setLivePts([toDesignPoint(e)]);
  };
  const onDrawMove = (e: React.PointerEvent) => {
    if (!drawingRef.current) return;
    setLivePts((prev) => (prev ? [...prev, toDesignPoint(e)] : [toDesignPoint(e)]));
  };
  const onDrawUp = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const pts = livePts ?? [];
    setLivePts(null);
    if (pts.length < 2) return;
    const cfg = tool === 'highlighter' ? HIGHLIGHTER : PEN;
    onStrokeComplete?.({
      rowId: slide.rows[0]?.id ?? '',
      points: pts,
      color: cfg.color,
      strokeWidth: cfg.width,
    });
  };

  return (
    // The stage is a landscape surface that grows with its content (ScaledStage
    // scales it to width; the parent scrolls when it's taller than the frame).
    <div ref={containerRef} className="relative w-full">
      <div
        style={{ background: slide.background ?? '#ffffff', minHeight: DESIGN_CANVAS.height }}
        className="relative w-full overflow-hidden rounded-2xl bg-white shadow-slide ring-1 ring-black/5"
      >
        <div className="absolute left-6 top-5 z-10 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-fg-subtle">
          {slide.label}
          <span className="h-1.5 w-1.5 rounded-full bg-success" />
        </div>

        <div className="flex w-full flex-col pt-12 pb-6" style={{ minHeight: DESIGN_CANVAS.height }}>
          {slide.rows.map((row) => {
            // Every column is a freeform canvas (mirrors the editor): columns sit
            // side by side at their widthPercent, sharing the row height, and each
            // element is absolutely positioned via effectiveFreeformRect.
            const heightPx = Math.max(120, (row.heightPercent / 100) * DESIGN_CANVAS.height);
            return (
              <div
                key={row.id}
                className="flex w-full gap-3"
                style={{
                  paddingTop: row.padding.top,
                  paddingRight: row.padding.right,
                  paddingBottom: row.padding.bottom,
                  paddingLeft: row.padding.left,
                }}
              >
                {row.columns.map((col) => (
                  <div
                    key={col.id}
                    style={{ width: `${col.widthPercent}%`, height: heightPx }}
                    className="relative overflow-hidden"
                  >
                    {col.elements.map((el, i) => {
                      const visible =
                        revealedElementIds === null || revealedElementIds.has(el.id);
                      const r = effectiveFreeformRect(el.position, i);
                      return (
                        <div
                          key={el.id}
                          ref={registerElement(el.id)}
                          data-element-id={el.id}
                          onClick={(e) => {
                            if (!interactive || !visible) return;
                            e.stopPropagation();
                            onPickElement(el.id);
                          }}
                          style={{
                            position: 'absolute',
                            left: r.x,
                            top: r.y,
                            width: r.w,
                            height: r.h,
                            transform: r.rotation ? `rotate(${r.rotation}deg)` : undefined,
                            zIndex: r.zIndex,
                          }}
                          className={cn(
                            'overflow-hidden rounded-md transition-all duration-300',
                            interactive && visible
                              ? 'cursor-crosshair ring-1 ring-dashed ring-accent/20 hover:ring-accent/60'
                              : '',
                            !visible && 'pointer-events-none opacity-0 translate-y-1',
                          )}
                        >
                          <StageElement element={el} fill />
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {/* Annotation overlay */}
      <div className="pointer-events-none absolute inset-0">
        {annotations.map((a) => {
          const b = boxes[a.targetElementId];
          if (!b) return null;
          if (revealedElementIds && !revealedElementIds.has(a.targetElementId)) return null;
          return <AnnotationMark key={a.id} annotation={a} rect={b} />;
        })}
      </div>

      {/* Freehand draw overlay — captures pointer only while a pen tool is on. */}
      <svg
        className="absolute inset-0 h-full w-full"
        style={{
          pointerEvents: drawing ? 'auto' : 'none',
          cursor: drawing ? 'crosshair' : 'default',
        }}
        onPointerDown={onDrawDown}
        onPointerMove={onDrawMove}
        onPointerUp={onDrawUp}
      >
        {drawStrokes.map((s) => (
          <polyline
            key={s.id}
            points={strokePointsAttr(s)}
            fill="none"
            stroke={s.color}
            strokeWidth={s.strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity={s.strokeWidth >= 12 ? 0.4 : 1}
          />
        ))}
        {livePts && livePts.length > 1 && (
          <polyline
            points={livePts.map((p) => `${p.x},${p.y}`).join(' ')}
            fill="none"
            stroke={tool === 'highlighter' ? HIGHLIGHTER.color : PEN.color}
            strokeWidth={tool === 'highlighter' ? HIGHLIGHTER.width : PEN.width}
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity={tool === 'highlighter' ? 0.4 : 1}
          />
        )}
      </svg>
    </div>
  );
}

function AnnotationMark({
  annotation,
  rect,
}: {
  annotation: Annotation;
  rect: { left: number; top: number; width: number; height: number };
}) {
  const color = annotation.color ?? '#facc15';

  if (annotation.kind === 'highlight') {
    return (
      <div
        className="absolute rounded-md mix-blend-multiply"
        style={{
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height,
          background: color,
          opacity: 0.35,
        }}
      />
    );
  }
  if (annotation.kind === 'underline') {
    return (
      <div
        className="absolute"
        style={{
          left: rect.left + 4,
          top: rect.top + rect.height - 2,
          width: rect.width - 8,
          height: 3,
          background: color,
          borderRadius: 2,
        }}
      />
    );
  }
  if (annotation.kind === 'encircle') {
    return (
      <div
        className="absolute rounded-full border-[3px]"
        style={{
          left: rect.left - 8,
          top: rect.top - 8,
          width: rect.width + 16,
          height: rect.height + 16,
          borderColor: color,
          borderStyle: 'dashed',
        }}
      />
    );
  }
  if (annotation.kind === 'zoom' || annotation.kind === 'spotlight') {
    // Dim everything except the target via a giant ring whose box-shadow fills
    // the stage, leaving the element's rect bright. For `zoom` this previews the
    // zoom the offline player performs (via zoomScale); `spotlight` only dims the
    // surroundings — same studio preview, no zoom in the player.
    return (
      <div
        className="absolute rounded-lg"
        style={{
          left: rect.left - 6,
          top: rect.top - 6,
          width: rect.width + 12,
          height: rect.height + 12,
          boxShadow: `0 0 0 9999px rgba(15, 23, 42, 0.55), 0 0 0 2px ${color}`,
        }}
      />
    );
  }
  if (annotation.kind === 'annotate') {
    return (
      <div
        className="absolute"
        style={{
          left: rect.left + rect.width + 12,
          top: rect.top,
          maxWidth: 220,
        }}
      >
        <div
          className="rounded-md px-2.5 py-1.5 text-[11px] font-medium text-white shadow-floating"
          style={{ background: color }}
        >
          {annotation.text || 'Note'}
        </div>
      </div>
    );
  }
  return null;
}
